import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './components/layout/AppShell';
import { Skeleton } from './components/ui/skeleton';
import { useAuth } from './lib/auth-context';
import { DashboardPage } from './pages/DashboardPage';
import { KanbanPage } from './pages/KanbanPage';
import { LeadCreatePage } from './pages/LeadCreatePage';
import { LeadDetailPage } from './pages/LeadDetailPage';
import { ActivatePage } from './pages/ActivatePage';
import { LoginPage } from './pages/LoginPage';
import { NotificationSettingsPage } from './pages/NotificationSettingsPage';
import { ProjectCreatePage } from './pages/ProjectCreatePage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ProjectsPage } from './pages/ProjectsPage';

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { status } = useAuth();
  if (status === 'loading') {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Skeleton className="h-32 w-64" />
      </div>
    );
  }
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }: { children: JSX.Element }): JSX.Element {
  const { status } = useAuth();
  if (status === 'authenticated') return <Navigate to="/dashboard" replace />;
  return children;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route path="/activate" element={<ActivatePage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell>
              <Navigate to="/dashboard" replace />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <AppShell>
              <DashboardPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/projects"
        element={
          <RequireAuth>
            <AppShell>
              <ProjectsPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/projects/new"
        element={
          <RequireAuth>
            <AppShell>
              <ProjectCreatePage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <RequireAuth>
            <AppShell>
              <ProjectDetailPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/projects/:id/board"
        element={
          <RequireAuth>
            <AppShell>
              <KanbanPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/projects/:id/leads/new"
        element={
          <RequireAuth>
            <AppShell>
              <LeadCreatePage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <RequireAuth>
            <AppShell>
              <LeadDetailPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <RequireAuth>
            <AppShell>
              <NotificationSettingsPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
